import React, { useEffect, useState } from "react";
import api from "../axiosSetup";
import { useNavigate, useLocation } from "react-router-dom";
import CustomNavbar from "../CustomNavbar";
import cfg from "../../config";

const ZRMoveEdit = () => {
  const [formData, setFormData] = useState({});
  const [formFields, setFormFields] = useState([]);
  const [title, setTitle] = useState("");
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState([]);
  const [id, setId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const move = location.state?.move;
  const mode = location.state?.mode || (move ? "edit" : "create");

  useEffect(() => {
    if (move?.id) setId(move.id);

    api
      .get("/api/form/ZRMoveEdit.jsx")
      .then((response) => {
        const fields = response.data.fields;
        const formTitle =
          mode === "create"
            ? `${cfg.eventTerm} erstellen`
            : response.data.title || `${cfg.eventTerm} bearbeiten`;

        setFormFields(fields);
        setTitle(formTitle);

        if (mode === "edit" && move?.fields) {
          const initialData = {};
          move.fields.forEach((field) => {
            const key = field.key;
            let val = field.value;

            switch (field.dataType) {
              case "date":
                if (val && /^\d{2}\.\d{2}\.\d{4}$/.test(val)) {
                  const [day, month, year] = val.split(".");
                  val = `${year}-${month}-${day}`;
                }
                break;
              case "datetime":
                if (val && !val.includes("T")) val = val.replace(" ", "T");
                break;
              case "boolean":
                val = Boolean(val);
                break;
              case "departures":
                // Array direkt übernehmen
                break;
              default:
                break;
            }
            initialData[key] = val ?? (field.dataType === "departures" ? [] : "");
          });
          setFormData(initialData);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("Fehler beim Laden des Formulars:", error);
        setErrors(["Fehler beim Laden des Formulars"]);
        setLoading(false);
      });
  }, [move, mode]);

  const handleInputChange = (e, fieldName) => {
    setFormData({ ...formData, [fieldName]: e.target.value });
  };

  const handleCheckboxChange = (e, fieldName) => {
    setFormData({ ...formData, [fieldName]: e.target.checked });
  };

  // Abfahrtsorte: Eintrag ändern
  const handleDepartureChange = (index, key, value) => {
    const updated = [...(formData.departures || [])];
    updated[index] = { ...updated[index], [key]: value };
    setFormData({ ...formData, departures: updated });
  };

  // Abfahrtsorte: neuen Eintrag hinzufügen
  const addDeparture = () => {
    setFormData({ ...formData, departures: [...(formData.departures || []), { label: "", time: "" }] });
  };

  // Abfahrtsorte: Eintrag entfernen
  const removeDeparture = (index) => {
    const updated = (formData.departures || []).filter((_, i) => i !== index);
    setFormData({ ...formData, departures: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);

    const submitData = {
      move: {
        id: mode === "edit" ? id : undefined,
        fields: Object.keys(formData).map((key) => ({
          key,
          value: formData[key],
        })),
      },
    };

    const endpoint =
      mode === "create" ? "/api/create-move" : "/api/update-move/" + id;

    try {
      const token = localStorage.getItem("authToken");
      const response = await api.post(endpoint, submitData, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.status !== 200) {
        setErrors([`Fehler: ${response.status} - ${response.statusText}`]);
        return;
      }

      navigate("/zr-moves", {
        state: {
          message:
            mode === "create"
              ? `${cfg.eventTerm} erfolgreich erstellt!`
              : `${cfg.eventTerm} erfolgreich bearbeitet!`,
        },
      });
    } catch (error) {
      console.error("Fehler beim Absenden:", error);
      if (error.response) {
        setErrors([
          `Fehler: ${error.response.status} - ${error.response.data.message || error.response.statusText}`,
        ]);
      } else {
        setErrors(["Fehler beim Absenden. Bitte erneut versuchen."]);
      }
    }
  };

  const renderFormFields = () => {
    if (formFields.length === 0) return <p>Keine Felder zum Anzeigen vorhanden.</p>;

    return formFields.map((field, index) => {
      const isDisabled = field.disabled === true;
      const value = formData[field.name] ?? "";

      switch (field.type) {
        case "text":
        case "date":
        case "time":
        case "datetime":
        case "number":
          return (
            <div className="mb-3" key={index}>
              <label htmlFor={field.name} className="form-label">{field.label}{field.required && <span className="text-danger ms-1">*</span>}</label>
              <input
                type={field.type === "datetime" ? "datetime-local" : field.type}
                className="form-control"
                id={field.name}
                name={field.name}
                value={value}
                onChange={(e) => handleInputChange(e, field.name)}
                required={field.required}
                disabled={isDisabled}
              />
            </div>
          );
        case "textarea":
          return (
            <div className="mb-3" key={index}>
              <label htmlFor={field.name} className="form-label">{field.label}{field.required && <span className="text-danger ms-1">*</span>}</label>
              <textarea
                className="form-control"
                id={field.name}
                name={field.name}
                rows={3}
                value={value}
                onChange={(e) => handleInputChange(e, field.name)}
                required={field.required}
                disabled={isDisabled}
              />
            </div>
          );
        case "checkbox":
          return (
            <div className="form-check mb-3" key={index}>
              <input
                className="form-check-input"
                type="checkbox"
                id={field.name}
                name={field.name}
                checked={formData[field.name] || false}
                onChange={(e) => handleCheckboxChange(e, field.name)}
                disabled={isDisabled}
              />
              <label className="form-check-label" htmlFor={field.name}>{field.label}{field.required && <span className="text-danger ms-1">*</span>}</label>
            </div>
          );
        case "departure-list": {
          const deps = formData.departures || [];
          return (
            <div className="mb-3" key={index}>
              <label className="form-label fw-semibold">{field.label}{field.required && <span className="text-danger ms-1">*</span>}</label>
              {deps.map((dep, i) => (
                <div key={i} className="d-flex gap-2 mb-2 align-items-center">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Ort (z.B. Maselheim)"
                    value={dep.label || ""}
                    onChange={(e) => handleDepartureChange(i, "label", e.target.value)}
                  />
                  <input
                    type="time"
                    className="form-control"
                    value={dep.time || ""}
                    onChange={(e) => handleDepartureChange(i, "time", e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-danger btn-sm"
                    onClick={() => removeDeparture(i)}
                  >✕</button>
                </div>
              ))}
              <button type="button" className="btn btn-outline-secondary btn-sm mt-1" onClick={addDeparture}>
                + Abfahrtsort hinzufügen
              </button>
            </div>
          );
        }
        default:
          return null;
      }
    });
  };

  if (loading) return <div>Lade Formular...</div>;

  return (
    <div className="container mt-4">
      <CustomNavbar />
      <h2>{title}</h2>

      {errors.length > 0 && (
        <div className="alert alert-danger">
          {errors.map((err, idx) => (
            <div key={idx}>{err}</div>
          ))}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {renderFormFields()}
        <button type="submit" className="btn btn-primary">Speichern</button>
      </form>
      <br/>
      <br/>
    </div>
  );
};

export default ZRMoveEdit;
